import type { ReactNode } from 'react';
import { useNarrow } from '@/components/list/use-narrow';
import { cn } from '@/lib/utils';

export interface MileageColumn<T> {
  readonly header: string;
  readonly cell: (item: T) => ReactNode;
  readonly numeric?: boolean;
}

/**
 * The mileage lists' one layout, the same as the expense list's: a table with grey capital
 * headers from 620 px of shell width up, a card per row below it (M17). Each row ends in its
 * actions, kept next to the data rather than a screen away.
 */
export function MileageList<T>({
  label,
  items,
  rowKey,
  columns,
  card,
  actions,
}: {
  readonly label: string;
  readonly items: readonly T[];
  readonly rowKey: (item: T) => string;
  readonly columns: readonly MileageColumn<T>[];
  readonly card: (item: T) => { readonly title: ReactNode; readonly detail: ReactNode };
  readonly actions: (item: T) => ReactNode;
}) {
  if (useNarrow()) {
    return (
      <ul aria-label={label} className="flex flex-col gap-2">
        {items.map((item) => {
          const { title, detail } = card(item);
          return (
            <li
              key={rowKey(item)}
              className="border-border bg-card flex flex-col gap-2 rounded-xl border p-3"
            >
              <span className="flex min-w-0 flex-col">
                <b className="break-words">{title}</b>
                <small className="text-muted-foreground tabular-nums">{detail}</small>
              </span>
              <span className="flex flex-wrap items-center justify-end gap-1">{actions(item)}</span>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <>
      <div className="border-border overflow-x-auto rounded-xl border">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{label}</caption>
          <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.header}
                  scope="col"
                  className={cn(
                    'px-3 py-2.5 font-semibold',
                    column.numeric === true && 'text-right',
                  )}
                >
                  {column.header}
                </th>
              ))}
              <th scope="col" className="px-3 py-2.5">
                <span className="sr-only">{label}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={rowKey(item)} className="border-border border-t">
                {columns.map((column) => (
                  <td
                    key={column.header}
                    className={cn(
                      'px-3 py-2',
                      column.numeric === true && 'text-right tabular-nums',
                    )}
                  >
                    {column.cell(item)}
                  </td>
                ))}
                <td className="px-3 py-2">
                  <span className="flex items-center justify-end gap-1">{actions(item)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
